import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, PenLine, LayoutTemplate } from "lucide-react";
import { api } from "@/lib/api";
import { CURRENCIES, currencyFromDestination } from "@/lib/currencies";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const UNSPLASH = "https://images.unsplash.com/photo-1602501522628-077df6282ac2?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function NewTripModal({ open, onClose, onCreated }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("ai");
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [destination, setDestination] = useState("");
  const [days, setDays] = useState(3);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [prompt, setPrompt] = useState("");
  const [tier, setTier] = useState("mid-budget");
  const [provider, setProvider] = useState("gemini");
  const [currency, setCurrency] = useState("USD");
  const [currencyTouched, setCurrencyTouched] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [cloneBusy, setCloneBusy] = useState(null);

  // Auto-suggest currency from destination unless the user manually chose one
  React.useEffect(() => {
    if (!currencyTouched) {
      const guess = currencyFromDestination(destination);
      if (guess && guess !== currency) setCurrency(guess);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination]);

  React.useEffect(() => {
    if (open && templates.length === 0) {
      api.get("/templates").then(r => setTemplates(r.data.templates)).catch(() => {});
    }
  }, [open, templates.length]);

  const reset = () => { setTitle(""); setDestination(""); setDays(3); setStart(""); setEnd(""); setPrompt(""); setTier("mid-budget"); setCurrency("USD"); setCurrencyTouched(false); };

  const handleManual = async () => {
    if (!title || !destination) { toast.error("Title and destination are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/trips", {
        title, destination, start_date: start, end_date: end, days: Number(days) || 3, cover_image: UNSPLASH, currency
      });
      toast.success("Trip created");
      onCreated?.(data); reset(); onClose();
      navigate(`/trips/${data.trip_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const handleAI = async () => {
    if (!destination || !prompt) { toast.error("Destination and prompt required"); return; }
    setBusy(true);
    let tripId = null;
    try {
      // 1) Generate itinerary first — no trip shell is created (or counted) if this fails
      const { data: gen } = await api.post("/ai/generate-itinerary", {
        prompt, destination, days: Number(days) || 3, budget_tier: tier, provider,
      });
      // 2) Create the trip shell
      const { data: trip } = await api.post("/trips", {
        title: title || gen.title || `${destination} adventure`,
        destination, days: Number(days) || 3, start_date: start, end_date: end, cover_image: UNSPLASH, currency,
      });
      tripId = trip.trip_id;
      // 3) Save into trip
      await api.post(`/trips/${tripId}/save-ai-itinerary`, {
        days: gen.days, description: gen.description, cover_image: gen.cover_image || UNSPLASH,
      });
      toast.success("Itinerary generated");
      onCreated?.(trip); reset(); onClose();
      navigate(`/trips/${tripId}`);
    } catch (e) {
      // Roll back the orphan shell if step 2/3 failed so it doesn't consume the free trip slot
      if (tripId) { try { await api.delete(`/trips/${tripId}`); } catch {} }
      toast.error(e?.response?.data?.detail || "AI failed");
    }
    finally { setBusy(false); }
  };

  const cloneTemplate = async (tid) => {
    setCloneBusy(tid);
    try {
      const { data } = await api.post(`/templates/${tid}/clone`, {});
      toast.success("Trip cloned");
      onCreated?.(data); onClose();
      navigate(`/trips/${data.trip_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Clone failed"); }
    finally { setCloneBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="max-w-2xl bg-[#FAF7F2]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl">New trip</DialogTitle>
          <DialogDescription>Craft your journey manually, let Gemini 3 draft it, or clone a curated template.</DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="grid grid-cols-3 bg-stone-100">
            <TabsTrigger value="ai" data-testid="new-trip-ai-option"><Sparkles size={14} className="mr-1.5"/> AI</TabsTrigger>
            <TabsTrigger value="template" data-testid="new-trip-template-option"><LayoutTemplate size={14} className="mr-1.5"/> Templates</TabsTrigger>
            <TabsTrigger value="manual" data-testid="new-trip-manual-option"><PenLine size={14} className="mr-1.5"/> Manual</TabsTrigger>
          </TabsList>

          <TabsContent value="template" className="space-y-3 mt-4 max-h-[60vh] overflow-y-auto">
            {templates.length === 0 ? (
              <div className="text-stone-500 text-sm">Loading templates…</div>
            ) : templates.map(t => (
              <div key={t.id} className="rounded-2xl border border-stone-200 bg-white overflow-hidden flex flex-col sm:flex-row" data-testid={`template-${t.id}`}>
                <div className="sm:w-40 h-32 sm:h-auto bg-stone-200 shrink-0">
                  <img src={t.cover_image} alt="" className="w-full h-full object-cover"/>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="font-mono-code text-[10px] uppercase tracking-widest text-amber-700">{(t.tags || []).join(" · ") || "Template"}</div>
                  <div className="font-serif-display text-xl font-semibold mt-1">{t.title}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{t.destination} · {t.days_count} days</div>
                  <p className="text-sm text-stone-600 mt-2 leading-relaxed">{t.description}</p>
                  <div className="mt-3">
                    <Button size="sm" onClick={() => cloneTemplate(t.id)} disabled={!!cloneBusy} className="bg-[#C85A32] hover:bg-[#B04B26]" data-testid={`template-clone-${t.id}`}>
                      {cloneBusy === t.id ? "Cloning…" : "Clone this trip"}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="ai" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Destination</Label>
                <Input value={destination} onChange={(e)=>setDestination(e.target.value)} placeholder="Tokyo, Japan" className="mt-1" data-testid="ai-destination-input"/>
              </div>
              <div>
                <Label>Days</Label>
                <Input type="number" min={1} max={14} value={days} onChange={(e)=>setDays(e.target.value)} className="mt-1" data-testid="ai-days-input"/>
              </div>
            </div>
            <div>
              <Label>Prompt / style</Label>
              <Textarea value={prompt} onChange={(e)=>setPrompt(e.target.value)} placeholder="5 days in Tokyo, mid-budget, foodie, one temple day, jazz bars at night." className="mt-1 min-h-24" data-testid="ai-prompt-input"/>
            </div>
            <div>
              <Label>Budget tier</Label>
              <div className="flex gap-2 mt-1">
                {["backpacker","mid-budget","boutique","luxury"].map(t=>(
                  <button key={t} type="button" onClick={()=>setTier(t)} data-testid={`tier-${t}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-mono-code uppercase tracking-wider border ${tier===t?"bg-stone-900 text-white border-stone-900":"bg-white border-stone-300 text-stone-700"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>AI model</Label>
              <div className="flex gap-2 mt-1">
                {[{v:"gemini",l:"Gemini 3 Flash",n:"Fast & concise"},{v:"claude",l:"Claude Sonnet 4.6",n:"Richer voice, longer"}].map(m=>(
                  <button key={m.v} type="button" onClick={()=>setProvider(m.v)} data-testid={`ai-model-${m.v}`}
                    className={`flex-1 text-left px-3 py-2 rounded-xl border ${provider===m.v?"bg-stone-900 text-white border-stone-900":"bg-white border-stone-300 text-stone-700"}`}>
                    <div className="font-semibold text-sm">{m.l}</div>
                    <div className={`text-[10px] font-mono-code uppercase tracking-widest ${provider===m.v?"text-stone-300":"text-stone-500"}`}>{m.n}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Trip currency</Label>
              <select
                value={currency}
                onChange={(e)=>{ setCurrency(e.target.value); setCurrencyTouched(true); }}
                data-testid="ai-currency-select"
                className="mt-1 w-full h-10 rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.name}</option>
                ))}
              </select>
              <p className="mt-1 text-[11px] font-mono-code text-stone-500">Auto-detected from destination. Change if the traveler pays in a different currency.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={handleAI} disabled={busy} className="bg-[#C85A32] hover:bg-[#B04B26]" data-testid="ai-generate-submit-button">
                <Sparkles size={14} className="mr-1.5"/> {busy?"Weaving your trip…":"Generate itinerary"}
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="manual" className="space-y-4 mt-4">
            <div>
              <Label>Title</Label>
              <Input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="Kyoto in Autumn" className="mt-1" data-testid="manual-title-input"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Destination</Label>
                <Input value={destination} onChange={(e)=>setDestination(e.target.value)} placeholder="Kyoto, Japan" className="mt-1" data-testid="manual-destination-input"/>
              </div>
              <div>
                <Label>Days</Label>
                <Input type="number" min={1} max={30} value={days} onChange={(e)=>setDays(e.target.value)} className="mt-1" data-testid="manual-days-input"/>
              </div>
              <div>
                <Label>Start</Label>
                <Input type="date" value={start} onChange={(e)=>setStart(e.target.value)} className="mt-1"/>
              </div>
              <div>
                <Label>End</Label>
                <Input type="date" value={end} onChange={(e)=>setEnd(e.target.value)} className="mt-1"/>
              </div>
            </div>
            <div>
              <Label>Trip currency</Label>
              <select
                value={currency}
                onChange={(e)=>{ setCurrency(e.target.value); setCurrencyTouched(true); }}
                data-testid="manual-currency-select"
                className="mt-1 w-full h-10 rounded-md border border-stone-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.name}</option>
                ))}
              </select>
              <p className="mt-1 text-[11px] font-mono-code text-stone-500">Suggested from your destination — change if needed.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button onClick={handleManual} disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="manual-create-submit-button">
                {busy?"Creating…":"Create trip"}
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
